import { useState, useCallback, useEffect, useRef } from 'react'
import {
  EnterpriseDetail,
  EnterpriseSummary,
  RelatedEnterprise,
} from '../clients/kyousou_api_client'
import { Enterprise, Kuchikomi } from '../clients/kuchikomi_api_client'
import { getKyousouApiClient, getKuchikomiApiClient } from '../services/network'

/**
 * 企業詳細情報を取得するフック
 * Kyousou/Kuchikomi APIから企業詳細、関連企業、クチコミデータを取得
 */
export const useEnterpriseDetail = (enterpriseId: string | undefined) => {
  // ========================================
  // State管理
  // ========================================
  const [enterprise, setEnterprise] = useState<EnterpriseDetail | null>(null)
  const [enterpriseSummary, setEnterpriseSummary] =
    useState<EnterpriseSummary | null>(null)
  const [kuchikomiEnterprise, setKuchikomiEnterprise] =
    useState<Enterprise | null>(null)
  const [kuchikomis, setKuchikomis] = useState<Kuchikomi[]>([])
  const [relatedEnterprises, setRelatedEnterprises] = useState<
    RelatedEnterprise[]
  >([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const apiClientRef = useRef(getKyousouApiClient())
  const kuchikomiApiClientRef = useRef(getKuchikomiApiClient())

  // ========================================
  // データ取得処理
  // ========================================
  const fetchEnterpriseDetail = useCallback(async (id: string) => {
    setLoading(true)
    setError(null)

    try {
      // Kyousou API: 企業詳細と関連企業
      const response =
        await apiClientRef.current.enterprise.enterpriseDetail(id)
      setEnterprise(response.data.detail || null)
      setEnterpriseSummary(response.data.enterpriseSummary || null)

      try {
        const relatedResponse =
          await apiClientRef.current.enterprise.relatedList(id)
        setRelatedEnterprises(relatedResponse.data.relatedEnterprises)
      } catch (relatedError) {
        // 関連企業データ取得のエラーは無視
      }

      // Kuchikomi API: 企業情報とクチコミ
      try {
        const kuchikomiResponse =
          await kuchikomiApiClientRef.current.enterprises.enterprisesDetail(id)
        setKuchikomiEnterprise(kuchikomiResponse.data)

        try {
          const kuchikomisResponse =
            await kuchikomiApiClientRef.current.enterprises.kuchikomisList(id, {
              cursor: 0,
              limit: 3,
            })
          setKuchikomis(kuchikomisResponse.data.data)
        } catch (kuchikomisError) {
          setKuchikomis([])
        }
      } catch (kuchikomiError) {
        setKuchikomiEnterprise(null)
        setKuchikomis([])
      }
    } catch (error) {
      console.error('企業基本情報取得エラー:', error)
      setError('企業基本情報の取得に失敗しました')
      setEnterprise(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // ========================================
  // Effect Hook
  // ========================================
  useEffect(() => {
    if (enterpriseId) {
      fetchEnterpriseDetail(enterpriseId)
    } else {
      setEnterprise(null)
      setEnterpriseSummary(null)
      setRelatedEnterprises([])
      setKuchikomiEnterprise(null)
      setKuchikomis([])
      setError(null)
    }
  }, [enterpriseId, fetchEnterpriseDetail])

  return {
    enterprise,
    enterpriseSummary,
    setEnterprise,
    relatedEnterprises,
    kuchikomiEnterprise,
    kuchikomis,
    loading,
    error,
  }
}
